import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Image,
  Upload,
  CheckCircle,
  AlertTriangle,
  Download,
  X,
  Sparkles,
  Shield,
  MapPin,
  Camera,
  Calendar,
  Tag,
  Eye,
  ChevronDown,
  ChevronUp,
  Info,
  Scan,
} from 'lucide-react';
import {
  analyzeImage,
  processGeminiImage,
  downloadCleanedImage,
  isValidImageFile,
  getReadableFileSize,
  revokeUrls,
  terminateImageWorker,
} from '../utils/imageCleanerWorker';
import type { AnalysisResult, ProcessResult } from '../utils/imageCleanerWorker';
import { useLocaleContext } from '../context/LocaleContext';
import { getTranslation } from '../utils/translations';
import { useSubscription } from '../hooks/useSubscription';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import type { User } from '@supabase/supabase-js';

interface ImageCleanerWidgetProps {
  user: User | null;
  isGridLayout?: boolean;
}

type WidgetState = 'idle' | 'analyzing' | 'preview' | 'processing' | 'ready' | 'success' | 'error';

const ImageCleanerWidget: React.FC<ImageCleanerWidgetProps> = ({ user, isGridLayout = false }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [widgetState, setWidgetState] = useState<WidgetState>('idle');
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [showAllMetadata, setShowAllMetadata] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [LoginModal, setLoginModal] = useState<React.ComponentType<{ isOpen: boolean; onClose: () => void; documentType: string }> | null>(null);
  const [processResult, setProcessResult] = useState<ProcessResult | null>(null);
  const [scanProgress, setScanProgress] = useState(0);
  const [isScanning, setIsScanning] = useState(false);

  const { locale } = useLocaleContext();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscription();

  const hasPremium = hasActiveSubscription();

  // Dynamically load login modal
  useEffect(() => {
    if (showLoginModal && !LoginModal) {
      import('./DocumentCleanerLoginModal').then((module) => {
        setLoginModal(() => module.default);
      });
    }
  }, [showLoginModal, LoginModal]);

  // Auto-proceed after popup login completes
  // When the popup signs in, user becomes non-null → close modal → re-trigger download
  useEffect(() => {
    if (user && showLoginModal && widgetState === 'ready' && processResult) {
      setShowLoginModal(false);
      setTimeout(() => handleDownload(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Track results in refs for cleanup
  const analysisResultRef = useRef<AnalysisResult | null>(null);
  analysisResultRef.current = analysisResult;
  const processResultRef = useRef<ProcessResult | null>(null);
  processResultRef.current = processResult;
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      if (analysisResultRef.current) {
        revokeUrls(analysisResultRef.current.previewUrl, analysisResultRef.current.originalUrl);
      }
      if (processResultRef.current) {
        revokeUrls(processResultRef.current.cleanedUrl, processResultRef.current.originalUrl);
      }
      terminateImageWorker();
    };
  }, []);

  const resetState = useCallback(() => {
    if (analysisResult) {
      revokeUrls(analysisResult.previewUrl, analysisResult.originalUrl);
    }
    if (processResult) {
      revokeUrls(processResult.cleanedUrl, processResult.originalUrl);
    }
    setSelectedFile(null);
    setWidgetState('idle');
    setProgressMessage('');
    setError(null);
    setAnalysisResult(null);
    setProcessResult(null);
    setShowAllMetadata(false);
  }, [analysisResult, processResult]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setError(null);

      if (!isValidImageFile(file)) {
        const invalidFormatMessages: Record<string, string> = {
          en: 'Invalid format. Please upload a JPEG, PNG, or WebP image.',
          de: 'Ungültiges Format. Bitte laden Sie ein JPEG, PNG oder WebP Bild hoch.',
          'pt-BR': 'Formato inválido. Por favor, envie uma imagem JPEG, PNG ou WebP.',
          'ms-MY': 'Format tidak sah. Sila muat naik imej JPEG, PNG atau WebP.',
          es: 'Formato no válido. Por favor, sube una imagen JPEG, PNG o WebP.',
          fr: 'Format invalide. Veuillez télécharger une image JPEG, PNG ou WebP.',
          ja: '無効な形式です。JPEG、PNG、またはWebP画像をアップロードしてください。',
          id: 'Format tidak valid. Silakan unggah gambar JPEG, PNG, atau WebP.',
        };
        setError(invalidFormatMessages[locale] || invalidFormatMessages.en);
        return;
      }

      // Check file size (max 20MB for images)
      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        const fileTooLargeMessages: Record<string, string> = {
          en: 'File too large. Maximum size is 20MB.',
          de: 'Datei zu groß. Maximale Größe ist 20MB.',
          'pt-BR': 'Arquivo muito grande. Tamanho máximo é 20MB.',
          'ms-MY': 'Fail terlalu besar. Saiz maksimum ialah 20MB.',
          es: 'Archivo demasiado grande. El tamaño máximo es 20MB.',
          fr: 'Fichier trop volumineux. La taille maximale est de 20 Mo.',
          ja: 'ファイルが大きすぎます。最大サイズは20MBです。',
          id: 'File terlalu besar. Ukuran maksimum adalah 20MB.',
        };
        setError(fileTooLargeMessages[locale] || fileTooLargeMessages.en);
        return;
      }

      setSelectedFile(file);
      setWidgetState('analyzing');

      try {
        const result = await analyzeImage(file, (msg) => setProgressMessage(msg));
        setAnalysisResult(result);
        setWidgetState('preview');
        setProgressMessage('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Analysis failed');
        setWidgetState('error');
      }
    },
    [locale]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  // Process image (free for everyone) — no download yet
  const handleClean = async () => {
    if (!selectedFile || isScanning) return;

    // Calculate scan duration based on file size (1.5-3 seconds)
    const fileSizeKB = selectedFile.size / 1024;
    const minDuration = 1500;
    const maxDuration = 3000;
    const scanDuration = Math.min(
      maxDuration,
      minDuration + (fileSizeKB / 5000) * (maxDuration - minDuration)
    );

    // Start scanning animation
    setIsScanning(true);
    setScanProgress(0);
    setError(null);

    // Animate progress from 0 to 90% during scan duration
    const progressInterval = 50;
    const progressSteps = scanDuration / progressInterval;
    const progressIncrement = 90 / progressSteps;
    let currentProgress = 0;

    const progressTimer = setInterval(() => {
      currentProgress += progressIncrement;
      if (currentProgress >= 90) {
        currentProgress = 90;
        clearInterval(progressTimer);
      }
      setScanProgress(Math.min(currentProgress, 90));
    }, progressInterval);

    await new Promise((resolve) => setTimeout(resolve, scanDuration));

    clearInterval(progressTimer);
    setScanProgress(95);

    setWidgetState('processing');

    try {
      const result = await processGeminiImage(selectedFile, (msg) => setProgressMessage(msg));

      setScanProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 200));

      setProcessResult(result);
      setWidgetState('ready');
      setProgressMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Processing failed');
      setWidgetState('error');
    } finally {
      setIsScanning(false);
      setScanProgress(0);
    }
  };

  // Download cleaned image — paywall gate
  const handleDownload = async () => {
    if (!processResult) return;

    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (!hasPremium) {
      setShowPremiumModal(true);
      return;
    }

    setError(null);

    try {
      await downloadCleanedImage(processResult.cleanedBlob, processResult.filename);

      setWidgetState('success');
      const successMessages: Record<string, string> = {
        en: 'Success! Your cleaned image has been downloaded.',
        de: 'Erfolgreich! Ihr bereinigtes Bild wurde heruntergeladen.',
        'pt-BR': 'Sucesso! Sua imagem limpa foi baixada.',
        'ms-MY': 'Berjaya! Imej anda yang telah dibersihkan telah dimuat turun.',
        es: '¡Éxito! Tu imagen limpia ha sido descargada.',
        fr: 'Succès ! Votre image nettoyée a été téléchargée.',
        ja: '成功！クリーンな画像がダウンロードされました。',
        id: 'Berhasil! Gambar bersih Anda telah diunduh.',
      };
      setProgressMessage(successMessages[locale] || successMessages.en);

      resetTimerRef.current = setTimeout(resetState, 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
      setWidgetState('error');
    }
  };

  if (subscriptionLoading && user) {
    return (
      <div className="mt-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  const metadata = analysisResult?.metadata;
  const watermark = analysisResult?.watermark;

  return (
    <>
      {/* Scanning Animation Overlay */}
      {isScanning && (
        <div className="fixed inset-0 bg-neutral-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-900 p-6 sm:p-8 rounded-2xl shadow-xl max-w-sm w-full border border-neutral-200 dark:border-neutral-800 animate-scale-in">
            <div className="flex flex-col items-center mb-6">
              <div className="relative mb-5">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center border border-purple-100 dark:border-purple-800">
                  <Scan className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="absolute -inset-1 rounded-2xl border-2 border-purple-500/30 animate-ping" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-1">
                {{
                  en: 'Processing Image...',
                  de: 'Bild wird verarbeitet...',
                  'pt-BR': 'Processando imagem...',
                  'ms-MY': 'Memproses imej...',
                  es: 'Procesando imagen...',
                  fr: 'Traitement de l\'image...',
                  ja: '画像を処理中...',
                  id: 'Memproses gambar...',
                }[locale] || 'Processing Image...'}
              </h3>
              <p className="text-neutral-500 dark:text-neutral-400 text-center text-sm">
                {{
                  en: 'Detecting and removing watermark',
                  de: 'Wasserzeichen erkennen und entfernen',
                  'pt-BR': 'Detectando e removendo marca d\'água',
                  'ms-MY': 'Mengesan dan membuang tanda air',
                  es: 'Detectando y eliminando marca de agua',
                  fr: 'Détection et suppression du filigrane',
                  ja: '透かしを検出して削除中',
                  id: 'Mendeteksi dan menghapus watermark',
                }[locale] || 'Detecting and removing watermark'}
              </p>
            </div>

            <div className="mb-5">
              <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                <span>{{
                  en: 'Progress',
                  de: 'Fortschritt',
                  'pt-BR': 'Progresso',
                  'ms-MY': 'Kemajuan',
                  es: 'Progreso',
                  fr: 'Progression',
                  ja: '進捗',
                  id: 'Kemajuan',
                }[locale] || 'Progress'}</span>
                <span className="font-medium text-neutral-700 dark:text-neutral-300">{Math.round(scanProgress)}%</span>
              </div>
              <div className="h-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full transition-all duration-150 ease-out"
                  style={{ width: `${scanProgress}%` }}
                />
              </div>
            </div>

            <div className="space-y-2">
              {[
                { threshold: 20, en: 'Analyzing image', de: 'Bild analysieren', 'pt-BR': 'Analisando imagem', 'ms-MY': 'Menganalisis imej', es: 'Analizando imagen', fr: 'Analyse de l\'image', ja: '画像を分析中', id: 'Menganalisis gambar' },
                { threshold: 50, en: 'Locating watermark', de: 'Wasserzeichen finden', 'pt-BR': 'Localizando marca d\'água', 'ms-MY': 'Mencari tanda air', es: 'Localizando marca de agua', fr: 'Localisation du filigrane', ja: '透かしを検出中', id: 'Mencari watermark' },
                { threshold: 80, en: 'Processing image', de: 'Bild verarbeiten', 'pt-BR': 'Processando imagem', 'ms-MY': 'Memproses imej', es: 'Procesando imagen', fr: 'Traitement de l\'image', ja: '画像を処理中', id: 'Memproses gambar' },
                { threshold: 100, en: 'Stripping metadata', de: 'Metadaten entfernen', 'pt-BR': 'Removendo metadados', 'ms-MY': 'Membuang metadata', es: 'Eliminando metadatos', fr: 'Suppression des métadonnées', ja: 'メタデータを削除中', id: 'Menghapus metadata' },
              ].map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2.5 text-sm transition-colors ${
                    scanProgress >= step.threshold
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-neutral-300 dark:text-neutral-600'
                  }`}
                >
                  <CheckCircle size={14} className={scanProgress >= step.threshold ? '' : 'opacity-40'} />
                  <span>{step[locale as keyof typeof step] || step.en}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div id="image-cleaner" className={`${!isGridLayout ? 'mt-12 md:mt-16' : 'flex-1'}`}>
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-800 overflow-hidden h-full flex flex-col">
          {/* Header */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 border-b border-neutral-100 dark:border-neutral-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
                  <Image className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {getTranslation(locale, 'imageCleaner.title')}
                  </h2>
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                    {getTranslation(locale, 'imageCleaner.subtitle')}
                  </p>
                </div>
              </div>

              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-xs font-medium border border-green-200 dark:border-green-800">
                <Shield size={12} />
                {{
                  en: '100% private',
                  de: '100% privat',
                  'pt-BR': '100% privado',
                  'ms-MY': '100% peribadi',
                  es: '100% privado',
                  fr: '100% privé',
                  ja: '100%プライベート',
                  id: '100% pribadi',
                }[locale] || '100% private'}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 flex-1">
            {/* Idle State - Drop Zone */}
            {widgetState === 'idle' && (
              <div
                className={`relative w-full min-h-[180px] rounded-xl transition-all duration-200 cursor-pointer ${
                  isDragging
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-400 dark:border-purple-600'
                    : 'bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 hover:border-purple-300 dark:hover:border-purple-600'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => document.getElementById('image-file-input')?.click()}
              >
                <input
                  id="image-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileInputChange}
                  className="hidden"
                />

                <div className="flex flex-col items-center justify-center p-6 text-center h-full min-h-[180px]">
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800 mb-4">
                    <Upload className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-1">
                    {getTranslation(locale, 'imageCleaner.uploadTitle')}
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                    {getTranslation(locale, 'imageCleaner.uploadDescription')}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-neutral-600 dark:text-neutral-400">
                    <span className="flex items-center gap-1">
                      <Image size={12} />
                      JPEG, PNG, WebP
                    </span>
                    <span>•</span>
                    <span>Max 20MB</span>
                  </div>
                </div>
              </div>
            )}

            {/* Analyzing State */}
            {widgetState === 'analyzing' && (
              <div className="flex flex-col items-center justify-center p-8 text-center min-h-[200px]">
                <div className="w-10 h-10 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {progressMessage || {
                    en: 'Analyzing image...',
                    de: 'Analysiere Bild...',
                    'pt-BR': 'Analisando imagem...',
                    'ms-MY': 'Menganalisis imej...',
                    es: 'Analizando imagen...',
                    fr: 'Analyse de l\'image...',
                    ja: '画像を分析中...',
                    id: 'Menganalisis gambar...',
                  }[locale] || 'Analyzing image...'}
                </p>
              </div>
            )}

            {/* Preview State - Show Analysis Results */}
            {widgetState === 'preview' && analysisResult && (
              <div className="space-y-4">
                {/* File Info */}
                <div className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
                      <Image className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-neutral-900 dark:text-white text-sm truncate">
                        {selectedFile?.name}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {analysisResult.width} × {analysisResult.height} • {selectedFile && getReadableFileSize(selectedFile.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={resetState}
                    className="p-1.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  >
                    <X size={16} className="text-neutral-500" />
                  </button>
                </div>

                {/* Two Column Layout: Watermark Preview + Metadata */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Watermark Preview */}
                  <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye size={16} className="text-purple-600 dark:text-purple-400" />
                      <h4 className="font-medium text-neutral-900 dark:text-white text-sm">
                        {watermark?.detected
                          ? {
                              en: 'Watermark Detected',
                              de: 'Wasserzeichen erkannt',
                              'pt-BR': 'Marca d\'água detectada',
                              'ms-MY': 'Tanda air dikesan',
                              es: 'Marca de agua detectada',
                              fr: 'Filigrane détecté',
                              ja: '透かしを検出',
                              id: 'Watermark terdeteksi',
                            }[locale] || 'Watermark Detected'
                          : {
                              en: 'No Watermark Found',
                              de: 'Kein Wasserzeichen',
                              'pt-BR': 'Nenhuma marca d\'água encontrada',
                              'ms-MY': 'Tiada tanda air dijumpai',
                              es: 'Sin marca de agua',
                              fr: 'Aucun filigrane trouvé',
                              ja: '透かしなし',
                              id: 'Tidak ada watermark',
                            }[locale] || 'No Watermark Found'}
                      </h4>
                    </div>

                    <div className="relative aspect-video bg-neutral-200 dark:bg-neutral-700 rounded-lg overflow-hidden">
                      <img
                        src={analysisResult.previewUrl}
                        alt="Preview with watermark highlighted"
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {watermark?.detected && watermark.region && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                        <Info size={12} />
                        <span>
                          {{
                            en: 'Size:',
                            de: 'Größe:',
                            'pt-BR': 'Tamanho:',
                            'ms-MY': 'Saiz:',
                            es: 'Tamaño:',
                            fr: 'Taille :',
                            ja: 'サイズ:',
                            id: 'Ukuran:',
                          }[locale] || 'Size:'} {watermark.region.size}×{watermark.region.size}px •{' '}
                          {{
                            en: 'Position: Bottom right',
                            de: 'Position: Unten rechts',
                            'pt-BR': 'Posição: Inferior direito',
                            'ms-MY': 'Kedudukan: Kanan bawah',
                            es: 'Posición: Inferior derecha',
                            fr: 'Position : Bas droite',
                            ja: '位置: 右下',
                            id: 'Posisi: Kanan bawah',
                          }[locale] || 'Position: Bottom right'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Metadata Found */}
                  <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag size={16} className="text-purple-600 dark:text-purple-400" />
                      <h4 className="font-medium text-neutral-900 dark:text-white text-sm">
                        {{
                          en: 'Metadata Found',
                          de: 'Metadaten gefunden',
                          'pt-BR': 'Metadados encontrados',
                          'ms-MY': 'Metadata ditemui',
                          es: 'Metadatos encontrados',
                          fr: 'Métadonnées trouvées',
                          ja: 'メタデータを検出',
                          id: 'Metadata ditemukan',
                        }[locale] || 'Metadata Found'}
                      </h4>
                      <span className="ml-auto text-xs text-neutral-500 bg-neutral-200 dark:bg-neutral-700 px-2 py-0.5 rounded">
                        {metadata?.tagCount || 0} tags
                      </span>
                    </div>

                    <div className="space-y-2">
                      {/* AI-Generated Content Indicator - PROMINENT */}
                      {metadata?.isAiGenerated && (
                        <div className="flex items-start gap-2 p-3 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg">
                          <Sparkles size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-purple-800 dark:text-purple-200">
                              {metadata.aiSource || 'AI-Generated Image'}
                            </p>
                            {metadata.digitalSourceType && (
                              <p className="text-xs text-purple-600 dark:text-purple-300 mt-0.5">
                                Source: {metadata.digitalSourceType}
                              </p>
                            )}
                            <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                              {{
                                en: 'AI metadata will be stripped',
                                de: 'AI-Metadaten werden entfernt',
                                'pt-BR': 'Metadados de IA serão removidos',
                                'ms-MY': 'Metadata AI akan dibuang',
                                es: 'Los metadatos de IA serán eliminados',
                                fr: 'Les métadonnées IA seront supprimées',
                                ja: 'AIメタデータは削除されます',
                                id: 'Metadata AI akan dihapus',
                              }[locale] || 'AI metadata will be stripped'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* GPS Warning */}
                      {metadata?.hasLocationData && (
                        <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <MapPin size={14} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                              {metadata.gps?.formatted}
                            </p>
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              {{
                                en: 'Location data will be removed',
                                de: 'Standortdaten werden entfernt',
                                'pt-BR': 'Dados de localização serão removidos',
                                'ms-MY': 'Data lokasi akan dibuang',
                                es: 'Los datos de ubicación serán eliminados',
                                fr: 'Les données de localisation seront supprimées',
                                ja: '位置データは削除されます',
                                id: 'Data lokasi akan dihapus',
                              }[locale] || 'Location data will be removed'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Key Metadata */}
                      <div className="space-y-1.5">
                        {metadata?.camera && (
                          <div className="flex items-center gap-2 text-xs">
                            <Camera size={12} className="text-neutral-500" />
                            <span className="text-neutral-600 dark:text-neutral-400">{metadata.camera}</span>
                          </div>
                        )}
                        {metadata?.dateTime && (
                          <div className="flex items-center gap-2 text-xs">
                            <Calendar size={12} className="text-neutral-500" />
                            <span className="text-neutral-600 dark:text-neutral-400">{metadata.dateTime}</span>
                          </div>
                        )}
                        {metadata?.software && (
                          <div className="flex items-center gap-2 text-xs">
                            <Sparkles size={12} className="text-neutral-500" />
                            <span className="text-neutral-600 dark:text-neutral-400">{metadata.software}</span>
                          </div>
                        )}
                      </div>

                      {/* Raw Tags - always show when present */}
                      {metadata?.rawTags && Object.keys(metadata.rawTags).length > 0 && (
                        <>
                          {Object.keys(metadata.rawTags).length > 5 && (
                            <button
                              onClick={() => setShowAllMetadata(!showAllMetadata)}
                              className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 mt-2"
                            >
                              {showAllMetadata ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              {showAllMetadata
                                ? {
                                    en: 'Show less',
                                    de: 'Weniger anzeigen',
                                    'pt-BR': 'Mostrar menos',
                                    'ms-MY': 'Tunjukkan kurang',
                                    es: 'Mostrar menos',
                                    fr: 'Afficher moins',
                                    ja: '少なく表示',
                                    id: 'Tampilkan lebih sedikit',
                                  }[locale] || 'Show less'
                                : {
                                    en: 'Show all',
                                    de: 'Alle anzeigen',
                                    'pt-BR': 'Mostrar todos',
                                    'ms-MY': 'Tunjukkan semua',
                                    es: 'Mostrar todo',
                                    fr: 'Tout afficher',
                                    ja: 'すべて表示',
                                    id: 'Tampilkan semua',
                                  }[locale] || 'Show all'}
                            </button>
                          )}
                          <div className="mt-2 max-h-64 overflow-y-auto bg-white dark:bg-neutral-800 rounded-lg p-2 border border-neutral-200 dark:border-neutral-700">
                            {Object.entries(metadata.rawTags).slice(0, showAllMetadata || Object.keys(metadata.rawTags).length <= 5 ? 20 : 5).map(([key, value]) => (
                              <div key={key} className="flex justify-between text-xs py-0.5 border-b border-neutral-100 dark:border-neutral-700 last:border-0">
                                <span className="text-neutral-500 truncate max-w-[40%]">{key}</span>
                                <span className="text-neutral-700 dark:text-neutral-300 truncate max-w-[55%]">{value}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {/* No metadata message */}
                      {metadata && metadata.tagCount === 0 && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 italic">
                          {{
                            en: 'No metadata found in this image',
                            de: 'Keine Metadaten gefunden',
                            'pt-BR': 'Nenhum metadado encontrado nesta imagem',
                            'ms-MY': 'Tiada metadata ditemui dalam imej ini',
                            es: 'No se encontraron metadatos en esta imagen',
                            fr: 'Aucune métadonnée trouvée dans cette image',
                            ja: 'この画像にはメタデータが見つかりませんでした',
                            id: 'Tidak ada metadata ditemukan dalam gambar ini',
                          }[locale] || 'No metadata found in this image'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Processing State */}
            {widgetState === 'processing' && (
              <div className="flex flex-col items-center justify-center p-8 text-center min-h-[200px]">
                <div className="w-10 h-10 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {progressMessage || {
                    en: 'Cleaning image...',
                    de: 'Bereinige Bild...',
                    'pt-BR': 'Limpando imagem...',
                    'ms-MY': 'Membersihkan imej...',
                    es: 'Limpiando imagen...',
                    fr: 'Nettoyage de l\'image...',
                    ja: '画像をクリーニング中...',
                    id: 'Membersihkan gambar...',
                  }[locale] || 'Cleaning image...'}
                </p>
              </div>
            )}

            {/* Ready State - Before/After Comparison */}
            {widgetState === 'ready' && processResult && (
              <div className="space-y-4">
                {/* File Info - same style as preview state */}
                <div className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-neutral-900 dark:text-white text-sm truncate">
                        {selectedFile?.name}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {getTranslation(
                          locale,
                          processResult.watermarkRemoved
                            ? 'imageCleaner.watermarkRemoved'
                            : 'imageCleaner.noWatermarkRemoved'
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={resetState}
                    className="p-1.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  >
                    <X size={16} className="text-neutral-500" />
                  </button>
                </div>

                {/* Before/After Comparison - two column layout matching preview style */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Original (with watermark highlighted) */}
                  <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye size={16} className="text-neutral-500 dark:text-neutral-400" />
                      <h4 className="font-medium text-neutral-900 dark:text-white text-sm">
                        {getTranslation(locale, 'imageCleaner.beforeLabel')}
                      </h4>
                    </div>
                    <div className="relative aspect-video bg-neutral-200 dark:bg-neutral-700 rounded-lg overflow-hidden">
                      <img
                        src={analysisResult?.previewUrl || processResult.originalUrl}
                        alt="Original with watermark highlighted"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Cleaned (watermarked preview, right-click disabled) */}
                  <div className="bg-green-50/50 dark:bg-green-900/10 rounded-xl p-4 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles size={16} className="text-green-600 dark:text-green-400" />
                      <h4 className="font-medium text-green-800 dark:text-green-200 text-sm">
                        {getTranslation(locale, 'imageCleaner.afterLabel')}
                      </h4>
                    </div>
                    <div
                      className={`relative aspect-video bg-neutral-200 dark:bg-neutral-700 rounded-lg overflow-hidden ${!hasPremium ? 'select-none' : ''}`}
                      onContextMenu={!hasPremium ? (e) => e.preventDefault() : undefined}
                      onDragStart={!hasPremium ? (e) => e.preventDefault() : undefined}
                    >
                      <img
                        src={processResult.cleanedUrl}
                        alt="Cleaned"
                        className={`w-full h-full object-contain ${!hasPremium ? 'pointer-events-none' : ''}`}
                        draggable={hasPremium}
                      />
                      {/* Repeating logo watermark pattern — only for non-premium users */}
                      {!hasPremium && (
                        <div
                          className="absolute inset-0 pointer-events-none opacity-40"
                          style={{
                            backgroundImage: 'url(/logo.png)',
                            backgroundSize: '80px',
                            backgroundRepeat: 'repeat',
                            transform: 'rotate(-30deg) scale(1.5)',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Metadata stripped summary */}
                {metadata && metadata.tagCount > 0 && (
                  <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag size={16} className="text-green-600 dark:text-green-400" />
                      <h4 className="font-medium text-neutral-900 dark:text-white text-sm">
                        {{
                          en: 'Metadata Stripped',
                          de: 'Metadaten entfernt',
                          'pt-BR': 'Metadados removidos',
                          'ms-MY': 'Metadata dibuang',
                          es: 'Metadatos eliminados',
                          fr: 'Métadonnées supprimées',
                          ja: 'メタデータ削除済み',
                          id: 'Metadata dihapus',
                        }[locale] || 'Metadata Stripped'}
                      </h4>
                      <span className="ml-auto text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded border border-green-200 dark:border-green-800">
                        {metadata.tagCount} tags
                      </span>
                    </div>

                    <div className="space-y-2">
                      {metadata.isAiGenerated && (
                        <div className="flex items-start gap-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <Sparkles size={14} className="text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-green-800 dark:text-green-200">
                              {metadata.aiSource || 'AI-Generated Image'}
                            </p>
                            <p className="text-xs text-green-700 dark:text-green-300">
                              {{
                                en: 'AI metadata removed',
                                de: 'AI-Metadaten entfernt',
                                'pt-BR': 'Metadados de IA removidos',
                                'ms-MY': 'Metadata AI dibuang',
                                es: 'Metadatos de IA eliminados',
                                fr: 'Métadonnées IA supprimées',
                                ja: 'AIメタデータ削除済み',
                                id: 'Metadata AI dihapus',
                              }[locale] || 'AI metadata removed'}
                            </p>
                          </div>
                        </div>
                      )}

                      {metadata.hasLocationData && (
                        <div className="flex items-start gap-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <MapPin size={14} className="text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-green-800 dark:text-green-200">
                              {metadata.gps?.formatted}
                            </p>
                            <p className="text-xs text-green-700 dark:text-green-300">
                              {{
                                en: 'Location data removed',
                                de: 'Standortdaten entfernt',
                                'pt-BR': 'Dados de localização removidos',
                                'ms-MY': 'Data lokasi dibuang',
                                es: 'Datos de ubicación eliminados',
                                fr: 'Données de localisation supprimées',
                                ja: '位置データ削除済み',
                                id: 'Data lokasi dihapus',
                              }[locale] || 'Location data removed'}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="space-y-1.5">
                        {metadata.camera && (
                          <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 line-through">
                            <Camera size={12} />
                            <span>{metadata.camera}</span>
                          </div>
                        )}
                        {metadata.dateTime && (
                          <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 line-through">
                            <Calendar size={12} />
                            <span>{metadata.dateTime}</span>
                          </div>
                        )}
                        {metadata.software && (
                          <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 line-through">
                            <Sparkles size={12} />
                            <span>{metadata.software}</span>
                          </div>
                        )}
                      </div>

                      {metadata.rawTags && Object.keys(metadata.rawTags).length > 0 && (
                        <>
                          {Object.keys(metadata.rawTags).length > 5 && (
                            <button
                              onClick={() => setShowAllMetadata(!showAllMetadata)}
                              className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 mt-2"
                            >
                              {showAllMetadata ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              {showAllMetadata
                                ? { en: 'Show less', de: 'Weniger anzeigen', 'pt-BR': 'Mostrar menos', 'ms-MY': 'Tunjukkan kurang', es: 'Mostrar menos', fr: 'Afficher moins', ja: '少なく表示', id: 'Tampilkan lebih sedikit' }[locale] || 'Show less'
                                : { en: 'Show all removed tags', de: 'Alle entfernten Tags anzeigen', 'pt-BR': 'Mostrar todas as tags removidas', 'ms-MY': 'Tunjukkan semua tag dibuang', es: 'Mostrar todas las etiquetas eliminadas', fr: 'Afficher toutes les balises supprimées', ja: '削除されたタグをすべて表示', id: 'Tampilkan semua tag yang dihapus' }[locale] || 'Show all removed tags'}
                            </button>
                          )}
                          <div className="mt-2 max-h-64 overflow-y-auto bg-white dark:bg-neutral-800 rounded-lg p-2 border border-neutral-200 dark:border-neutral-700">
                            {Object.entries(metadata.rawTags).slice(0, showAllMetadata || Object.keys(metadata.rawTags).length <= 5 ? 20 : 5).map(([key, value]) => (
                              <div key={key} className="flex justify-between text-xs py-0.5 border-b border-neutral-100 dark:border-neutral-700 last:border-0 text-neutral-400 line-through">
                                <span className="truncate max-w-[40%]">{key}</span>
                                <span className="truncate max-w-[55%]">{value}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Success State */}
            {widgetState === 'success' && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-start gap-3 animate-scale-in">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-200">
                    {{
                      en: 'Success!',
                      de: 'Erfolgreich!',
                      'pt-BR': 'Sucesso!',
                      'ms-MY': 'Berjaya!',
                      es: '¡Éxito!',
                      fr: 'Succès !',
                      ja: '成功！',
                      id: 'Berhasil!',
                    }[locale] || 'Success!'}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">{progressMessage}</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {widgetState === 'error' && error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-800 dark:text-red-200">
                    {{
                      en: 'Error',
                      de: 'Fehler',
                      'pt-BR': 'Erro',
                      'ms-MY': 'Ralat',
                      es: 'Error',
                      fr: 'Erreur',
                      ja: 'エラー',
                      id: 'Kesalahan',
                    }[locale] || 'Error'}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{error}</p>
                  <button
                    onClick={resetState}
                    className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    {{
                      en: 'Try again',
                      de: 'Erneut versuchen',
                      'pt-BR': 'Tentar novamente',
                      'ms-MY': 'Cuba lagi',
                      es: 'Intentar de nuevo',
                      fr: 'Réessayer',
                      ja: 'もう一度試す',
                      id: 'Coba lagi',
                    }[locale] || 'Try again'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 bg-neutral-50 dark:bg-neutral-800/30 border-t border-neutral-100 dark:border-neutral-800 mt-auto">
            {widgetState === 'ready' ? (
              <>
                <button
                  onClick={handleDownload}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
                >
                  <Download size={18} />
                  <span>
                    {user && !hasPremium
                      ? {
                          en: 'Upgrade to Download',
                          de: 'Zum Herunterladen upgraden',
                          'pt-BR': 'Faça upgrade para baixar',
                          'ms-MY': 'Naik taraf untuk muat turun',
                          es: 'Hazte Premium para descargar',
                          fr: 'Passez à Premium pour télécharger',
                          ja: 'ダウンロードするにはアップグレード',
                          id: 'Upgrade untuk mengunduh',
                        }[locale] || 'Upgrade to Download'
                      : getTranslation(locale, 'imageCleaner.downloadButton')}
                  </span>
                </button>
                <button
                  onClick={resetState}
                  className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl font-medium transition-colors text-sm"
                >
                  {getTranslation(locale, 'imageCleaner.processAnother')}
                </button>
              </>
            ) : (
              <button
                onClick={handleClean}
                disabled={widgetState !== 'preview' || !analysisResult || isScanning}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-neutral-300 dark:disabled:bg-neutral-700 text-white disabled:text-neutral-500 dark:disabled:text-neutral-400 rounded-xl font-medium transition-colors disabled:cursor-not-allowed"
              >
                {widgetState === 'processing' || isScanning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>{{
                      en: 'Processing...',
                      de: 'Verarbeite...',
                      'pt-BR': 'Processando...',
                      'ms-MY': 'Memproses...',
                      es: 'Procesando...',
                      fr: 'Traitement...',
                      ja: '処理中...',
                      id: 'Memproses...',
                    }[locale] || 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>{getTranslation(locale, 'imageCleaner.cleanButton')}</span>
                  </>
                )}
              </button>
            )}

            <p className="text-center text-xs text-neutral-500 dark:text-neutral-400 mt-3">
              {getTranslation(locale, 'imageCleaner.privacyNote')}
            </p>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {LoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          documentType="image"
        />
      )}

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="image"
      />
    </>
  );
};

export default ImageCleanerWidget;
